// Login form component
// Provides username/password authentication interface with Material Design styling
// CHANGE: Updated app name from "Gestion de Tâches" to "Tasq"
// CHANGE: Added password visibility toggle with icon and text
// CHANGE: Added forced password change modal support
// UPDATED: Migrated to centralized toast system for error messages
// UPDATED: Standardized button loading text to "En cours..."
// UPDATED: Replaced text logo with image logo (logo-tasq-fh-blanc.png)

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { signIn } from '../lib/auth';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { ForcedPasswordChangeModal } from './ForcedPasswordChangeModal';

export function LoginForm() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForcedPasswordChange, setShowForcedPasswordChange] = useState(false);
  const { refreshUser } = useAuth();
  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const result = await signIn(username, password);

    if (result.success) {
      if (result.mustChangePassword) {
        setShowForcedPasswordChange(true);
        setLoading(false);
      } else {
        await refreshUser();
      }
    } else {
      showToast(result.error || 'Une erreur est survenue', 'error');
      setLoading(false);
    }
  };

  const handlePasswordChangeSuccess = async () => {
    setShowForcedPasswordChange(false);
    await refreshUser();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-card w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white p-8">
          <img
            src="/logo-tasq-fh-blanc.png"
            alt="Tasq FH"
            className="h-12 w-auto object-contain mb-3"
          />
          <p className="text-primary-100 text-sm">Connectez-vous à votre compte</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Nom d'utilisateur
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="Entrez votre nom d'utilisateur"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Mot de passe
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 pr-32 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Entrez votre mot de passe"
                aria-describedby="password-visibility-toggle"
              />
              <button
                type="button"
                id="password-visibility-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <>
                    <EyeOff size={18} aria-hidden="true" />
                    <span>Masquer</span>
                  </>
                ) : (
                  <>
                    <Eye size={18} aria-hidden="true" />
                    <span>Afficher</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {loading ? 'En cours...' : 'Se connecter'}
          </button>

          <div className="pt-4 text-center text-sm text-gray-600">
            <p>Mot de passe oublié ? Contactez votre administrateur</p>
          </div>
        </form>
      </div>

      {showForcedPasswordChange && (
        <ForcedPasswordChangeModal
          onSuccess={handlePasswordChangeSuccess}
          reason="first_login"
        />
      )}
    </div>
  );
}
